// One offer (PLAN-offers.md §7.4): its identity, its current terms, and its versions. New terms for a version in
// use make the next version; its subscribers keep theirs until staff migrate them (with notice).
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateOnly, keyIn, money } from '../../services/format';
import { Alert, Badge, Button, Card, Cell, Checkbox, Confirm, Field, Input, PageTitle, Row, Select, Table, Textarea } from '../../components/ui';
import { TermsForm } from './TermsForm';
import { places, seatRange, tierSummary } from './OffersList';
import type { OfferAdmin, OfferVersion, Terms } from '../../types/api';

const NOTICE_DAYS = 30;

function inDays(days: number): string {
  const d = new Date(Date.now() + days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

export function OfferDetail() {
  const { id } = useParams();
  const offerId = Number(id);
  const { t } = useTranslation();
  const { data: offer, error, reload } = useAsync(() => api.offer(offerId), [offerId]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [pending, setPending] = useState<Terms | null>(null);

  const run = async (name: string, fn: () => Promise<string | undefined>) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      const done = await fn();
      if (done) setNotice(done);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert>{error}</Alert>;
  if (!offer) return <p className="text-faint">{t('common.loading')}</p>;

  const current = offer.terms;
  const saveTerms = (terms: Terms) =>
    void run('terms', async () => {
      const result = await api.setTerms(offer.id, terms);
      setPending(null);
      return result.new_version ? t('offers.versionCreated', { version: result.version.version }) : t('offers.termsSaved', { version: result.version.version });
    });

  return (
    <div>
      <PageTitle
        title={
          <>
            {offer.name} <span className="font-mono text-[13px] text-faint" data-testid="offer-key-currency">{keyIn(offer.key, current.currency)}</span> {!offer.active ? <Badge tone="bad">{t('offers.inactive')}</Badge> : offer.public ? <Badge tone="good">{t('offers.public')}</Badge> : <Badge>{t('offers.private')}</Badge>}
          </>
        }
        subtitle={t('offers.detailSubtitle', { version: current.version, count: offer.subscribers })}
      />
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-2">
        <Identity offer={offer} busy={busy === 'identity'} onSave={(patch) => void run('identity', async () => (await api.patchOffer(offer.id, patch), t('common.saved')))} />
        <Card title={t('offers.currentTerms', { version: current.version })}>
          <TermsForm
            initial={current}
            busy={busy === 'terms'}
            submitLabel={current.locked_at ? t('offers.saveAsNewVersion', { version: current.version + 1 }) : t('common.save')}
            onSubmit={(terms) => (current.locked_at ? setPending(terms) : saveTerms(terms))}
            notice={current.locked_at ? <Alert tone="warn">{t('offers.lockedNotice', { version: current.version, count: current.subscribers })}</Alert> : <Alert tone="good">{t('offers.unlockedNotice')}</Alert>}
          />
        </Card>
      </div>
      <Versions offer={offer} busy={busy} onMigrate={(from, to, date) => void run(`migrate-${from.id}`, async () => {
        const result = await api.migrateVersion(from.id, to.id, date ? new Date(`${date}T00:00:00`).toISOString() : null);
        return result.applied > 0 ? t('offers.migratedNow', { count: result.applied }) : t('offers.migrated', { count: result.scheduled, date: dateOnly(result.effective_from) });
      })} />
      <Confirm
        open={pending !== null}
        title={t('offers.newVersionTitle', { version: current.version + 1 })}
        message={t('offers.newVersionMessage', { version: current.version, count: current.subscribers })}
        confirmLabel={t('offers.saveAsNewVersion', { version: current.version + 1 })}
        busy={busy === 'terms'}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && saveTerms(pending)}
      />
    </div>
  );
}

type IdentityPatch = { name: string; description: string; sort: number; public: boolean; active: boolean; max_active_subscriptions: number | null };

function Identity({ offer, busy, onSave }: { offer: OfferAdmin; busy: boolean; onSave: (patch: IdentityPatch) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(offer.name);
  const [description, setDescription] = useState(offer.description);
  const [sort, setSort] = useState(String(offer.sort));
  const [isPublic, setPublic] = useState(offer.public);
  const [active, setActive] = useState(offer.active);
  const [limit, setLimit] = useState(offer.max_active_subscriptions === null ? '' : String(offer.max_active_subscriptions));
  useEffect(() => {
    setName(offer.name);
    setDescription(offer.description);
    setSort(String(offer.sort));
    setPublic(offer.public);
    setActive(offer.active);
    setLimit(offer.max_active_subscriptions === null ? '' : String(offer.max_active_subscriptions));
  }, [offer]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const max = limit.trim() ? Math.max(0, parseInt(limit, 10) || 0) : null;
    onSave({ name: name.trim(), description: description.trim(), sort: parseInt(sort, 10) || 0, public: isPublic, active, max_active_subscriptions: max });
  };
  return (
    <form onSubmit={submit}>
      <Card title={t('offers.identity')} actions={<Button type="submit" variant="primary" busy={busy} data-testid="identity-save">{t('common.save')}</Button>}>
        <div className="flex flex-col gap-3">
          <Field label={t('offers.name')}>
            <Input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('offers.description')} hint={t('offers.descriptionHint')}>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('offers.sort')}>
              <Input type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
            </Field>
            <Field label={t('offers.placesLimit')} hint={t('offers.placesTaken', { places: places(offer) })}>
              <Input type="number" min={0} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="∞" data-testid="identity-limit" />
            </Field>
          </div>
          <Checkbox label={t('offers.publicHint')} checked={isPublic} onChange={(e) => setPublic(e.target.checked)} data-testid="identity-public" />
          <Checkbox label={t('offers.activeHint')} checked={active} onChange={(e) => setActive(e.target.checked)} />
        </div>
      </Card>
    </form>
  );
}

function Versions({ offer, busy, onMigrate }: { offer: OfferAdmin; busy: string | null; onMigrate: (from: OfferVersion, to: OfferVersion, date: string) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const [from, setFrom] = useState<OfferVersion | null>(null);
  const [toId, setToId] = useState<number | ''>('');
  const [date, setDate] = useState(inDays(NOTICE_DAYS));
  // The API moves subscribers only within one currency (409 `currency_mismatch`); an offer's versions share its own.
  const targets = offer.versions.filter((v) => v.id !== from?.id && v.trial_days === null && v.currency === from?.currency);
  const to = targets.find((v) => v.id === toId) ?? null;
  const open = (v: OfferVersion) => {
    setFrom(v);
    setToId(offer.terms.id !== v.id ? offer.terms.id : '');
    setDate(inDays(NOTICE_DAYS));
  };
  return (
    <div className="mt-4">
      <Card title={t('offers.versions')}>
        <p className="mb-3 text-[12px] text-muted">{t('offers.versionsHint')}</p>
        <Table head={[t('offers.version'), t('offers.pricePerSeat'), t('offers.allowancePerSeat'), t('offers.tiers'), t('offers.userCap'), t('offers.seats'), t('offers.createdAt'), t('offers.locked'), t('offers.subscribers'), '']}>
          {offer.versions.map((v) => (
            <Row key={v.id}>
              <Cell mono>
                v{v.version} {v.id === offer.terms.id && <Badge tone="good">{t('offers.current')}</Badge>}
              </Cell>
              <Cell mono>{v.trial_days !== null ? t('offers.trialFor', { days: v.trial_days }) : money(v.price_per_seat, v.currency, l)}</Cell>
              <Cell mono>{v.allowance_per_seat_eur === null ? t('orgs.unlimited') : money(v.allowance_per_seat_eur, v.currency, l)}</Cell>
              <Cell mono className="max-w-[240px] truncate" title={tierSummary(v, l)}>
                {tierSummary(v, l)}
              </Cell>
              <Cell mono>{v.user_cap_eur === null ? '—' : money(v.user_cap_eur, v.currency, l)}</Cell>
              <Cell mono>{seatRange(v)}</Cell>
              <Cell mono>{dateOnly(v.created_at, l)}</Cell>
              <Cell mono>{v.locked_at ? dateOnly(v.locked_at, l) : '—'}</Cell>
              <Cell mono>{v.subscribers}</Cell>
              <Cell>
                {v.subscribers > 0 && v.trial_days === null && offer.versions.length > 1 && (
                  <Button variant="ghost" busy={busy === `migrate-${v.id}`} onClick={() => open(v)} data-testid={`migrate-${v.version}`}>
                    {t('offers.migrate')}
                  </Button>
                )}
              </Cell>
            </Row>
          ))}
        </Table>
      </Card>
      {from && (
        <Confirm
          open
          title={t('offers.migrateTitle', { version: from.version, count: from.subscribers })}
          message={
            <div className="flex flex-col gap-3">
              <p>{t('offers.migrateMessage')}</p>
              <Field label={t('offers.migrateTo')}>
                <Select value={toId} onChange={(e) => setToId(e.target.value ? Number(e.target.value) : '')} data-testid="migrate-to">
                  <option value="">{t('billing.chooseVersion')}</option>
                  {targets.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} — {money(v.price_per_seat, v.currency, l)} {t('billing.perSeat')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('offers.migrateFrom')} hint={t('offers.migrateFromHint', { days: NOTICE_DAYS })}>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
            </div>
          }
          confirmLabel={t('offers.migrate')}
          busy={busy === `migrate-${from.id}`}
          onCancel={() => setFrom(null)}
          onConfirm={() => {
            if (!to) return;
            onMigrate(from, to, date);
            setFrom(null);
          }}
        />
      )}
    </div>
  );
}
